import _ from 'lodash'
import type {ComponentContext, InnerElementContext} from '../types'
import {getFeatureValue, DATA_FEATURE} from '../utils'

const DATA_TYPE_SUFFIX = '.Data'
const DATA_NAMESPACE = 'Data'

interface NestedData {
    type: string
    id: string
    data: Record<string, unknown>
}

interface FlattenedData {
    _id: string
    [key: string]: unknown
}

/**
 * Check if data has nested builder structure (type ending with .Data)
 */
const isNestedBuilderData = (data: unknown): data is NestedData => {
    if (!data || !_.isPlainObject(data)) {
        return false
    }
    const d = data as Record<string, unknown>
    return _.isString(d.type) && d.type.endsWith(DATA_TYPE_SUFFIX) && _.isString(d.id) && _.isPlainObject(d.data)
}

/**
 * Check if data is flattened (has _id and no type)
 */
const isFlattenedData = (data: unknown): data is FlattenedData => {
    if (!data || !_.isPlainObject(data)) {
        return false
    }
    const d = data as Record<string, unknown>
    return _.isString(d._id) && !d.type
}

/**
 * Flatten nested data: {type, id, data: {...}} → {_id, ...data}
 */
const flattenData = (data: NestedData): FlattenedData => {
    const {data: innerData, id} = data
    return {
        ...innerData,
        _id: id
    }
}

/**
 * Restore nested data: {_id, ...data} → {type, id, data: {...}}
 */
const restoreData = (data: FlattenedData, type: string): NestedData => {
    const {_id, ...innerData} = data
    return {
        type,
        id: _id,
        data: innerData
    }
}

/**
 * Build the data type string for component data
 * Convention: <componentType>.Data
 */
const buildComponentDataType = (componentType: string): string => {
    return `${componentType}.${DATA_NAMESPACE}`
}

/**
 * Build the data type string for innerElement data
 * Convention: <componentType>|<path>.Data
 */
const buildInnerElementDataType = (componentType: string, path: string[]): string => {
    return `${componentType}|${path.join('|')}.${DATA_NAMESPACE}`
}

// ============================================================
// Pack transformations (flatten nested structures)
// ============================================================

/**
 * Pack transformation for component data
 * Flattens nested builder data structure
 * Works with nested format: { structure: {...}, data: { data: {...} } }
 */
export const packComponentData = ({component}: ComponentContext): void => {
    const componentData = component.data?.data
    if (component.structure?.type === 'Component' && isNestedBuilderData(componentData)) {
        component.data.data = flattenData(componentData as NestedData)
    }
}

/**
 * Pack transformation for innerElement data
 * InnerElements data is always builder's data - flatten it
 * Uses COMPONENT_FEATURES to find data by either query or alias property name
 */
export const packInnerElementData = ({element}: InnerElementContext): void => {
    const dataResult = getFeatureValue(element, DATA_FEATURE)
    if (dataResult && isNestedBuilderData(dataResult.value)) {
        element[dataResult.propertyName] = flattenData(dataResult.value as NestedData)
    }
}

// ============================================================
// Unpack transformations (restore nested structures)
// ============================================================

/**
 * Unpack transformation for component data
 * Restores nested builder data structure
 * Works with nested format: { structure: {...}, data: { data: {...} } }
 */
export const unpackComponentData = ({component, componentType}: ComponentContext): void => {
    const componentData = component.data?.data
    if (component.structure?.type === 'Component' && isFlattenedData(componentData)) {
        const dataType = buildComponentDataType(componentType)
        component.data.data = restoreData(componentData as FlattenedData, dataType)
    }
}

/**
 * Unpack transformation for innerElement data
 * Restores nested builder data structure with constructed type
 * Uses COMPONENT_FEATURES to find data by either query or alias property name
 */
export const unpackInnerElementData = ({element, componentType, path}: InnerElementContext): void => {
    const dataResult = getFeatureValue(element, DATA_FEATURE)
    if (dataResult && isFlattenedData(dataResult.value)) {
        const dataType = buildInnerElementDataType(componentType, path)
        element[dataResult.propertyName] = restoreData(dataResult.value as FlattenedData, dataType)
    }
}
